import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { loadParcoursCatalog } from '../../services/dossiers/friseService';
import { useReplaceDossierFrise } from '../../hooks/queries/useDossiers';
import { todayInputValue } from '../../services/dossiers/stageDate';
import { landingCloses } from '../../lib/dossiers/friseReplace';

export function ChangeFriseDialog({
  dossierId,
  open,
  onClose,
}: {
  dossierId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data: families = [] } = useQuery({
    ...SEMI_FRESH,
    queryKey: ['dossier-parcours-catalog'],
    queryFn: loadParcoursCatalog,
    enabled: open,
  });
  const [familyId, setFamilyId] = useState<string | null>(null);
  const [friseId, setFriseId] = useState<string | null>(null);
  const [position, setPosition] = useState(1);
  const [effectiveOn, setEffectiveOn] = useState(todayInputValue);
  const [error, setError] = useState<string | null>(null);
  const replace = useReplaceDossierFrise();

  const family = families.find((item) => item.id === (familyId ?? families[0]?.id)) ?? null;
  const frises = family?.frises ?? [];
  const frise = frises.find((item) => item.id === friseId) ?? frises[0] ?? null;
  const steps = useMemo(
    () => (frise?.steps ?? []).map((label, index) => ({ position: index + 1, label })),
    [frise],
  );
  const closes = landingCloses(position, steps.length);

  function chooseFamily(id: string) {
    const next = families.find((item) => item.id === id);
    setFamilyId(id);
    setFriseId(next?.frises[0]?.id ?? null);
    setPosition(1);
  }

  function chooseFrise(id: string) {
    setFriseId(id);
    setPosition(1);
  }

  async function confirm() {
    if (!frise) return;
    setError(null);
    try {
      await replace.mutateAsync({
        id: dossierId,
        friseId: frise.id,
        position,
        effectiveOn,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'La frise n’a pas pu être changée.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Changer la frise</DialogTitle>
          <DialogDescription>
            Les étapes avant l’arrivée sont marquées faites, sans date. Seule l’étape choisie porte la date.
            {closes ? ' La dernière étape ne ferme pas le dossier : pour le sortir de la liste, archivez-le.' : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {families.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => chooseFamily(item.id)}
                className={`px-2.5 py-1 rounded-full text-xs border ${
                  item.id === family?.id
                    ? 'border-cime-primary bg-cime-primary/10 text-cime-primary'
                    : 'border-neutral-200 text-neutral-600'
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>
          <label className="block text-xs text-neutral-500">
            Frise
            <select
              value={frise?.id ?? ''}
              onChange={(event) => chooseFrise(event.target.value)}
              className="mt-1 w-full text-sm border border-neutral-200 rounded-md bg-white px-2 py-1.5"
            >
              {frises.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-neutral-500">
            Étape d’arrivée
            <select
              value={position}
              onChange={(event) => setPosition(Number(event.target.value))}
              className="mt-1 w-full text-sm border border-neutral-200 rounded-md bg-white px-2 py-1.5"
            >
              {steps.map((step) => (
                <option key={step.position} value={step.position}>{step.label}</option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-neutral-500">
            Date de cette étape
            <Input
              type="date"
              value={effectiveOn}
              onChange={(event) => setEffectiveOn(event.target.value)}
              className="mt-1"
            />
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={confirm} disabled={!frise || replace.isPending || !effectiveOn}>
            Changer la frise
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
